import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { observable, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { FormattedMessage } from 'react-intl';

import {
    useForm,
    Sizes,
    CalculatorStepPanel,
    CalculationPanel,
    CalculationLineBlock,
    MessageBanner,
    MessageBannerVariants,
    Question,
    LegacyCityPropType,
    LocationSelector,
    MessagePropType,
    isWidget,
    TOOLTIP_TOP_LOCATION,
    useCalculator,
    navigateWindowTo,
} from '@ratehub/base-ui';
import { MORTGAGE_PLACEHOLDERS } from '@ratehub/mtg-common';

import getLandTransferTaxCalculations from '../functions/getLandTransferTaxCalculations';


function LandTransferTaxStepInputs({
    stepNumber,

    initialProvinceCode,
    initialCity,
    initialAskingPrice,

    messageBannerTitle,
    messageBannerDescription,
    ctaHref,
    ctaLabel,

    isProductReferralDisabled,

    ...otherProps
}) {
    const state = useLocalState();

    // messageBannerDescription is required by the CTA MessageBanner at minimum.
    const isMessageBannerCTAEnabled = !isProductReferralDisabled && !!messageBannerDescription;

    return (
        <CalculatorStepPanel
            stepNumber={stepNumber}
            isVisible={true}

            {...otherProps}
        >
            <div className="inlineQuestion">
                {/* Asking Price */}
                <Question
                    className="askingPrice"
                    field={{
                        name: 'askingPrice',
                        type: 'string',
                        value: state.values.askingPrice ?? initialAskingPrice,
                    }}
                    labelMessage={(
                        <FormattedMessage
                            id="mtg-calculators.LandTransferTaxStepInputs.askingPriceLabel"
                            defaultMessage="Asking price"
                        />
                    )}
                    placeholder={MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER}
                    type="currency"
                />

                <Question
                    className="location"
                    id="land-transfer-tax-location"
                    field={{
                        name: 'location',
                        value: state.values.location ?? initialCity,
                    }}
                    type={LocationSelector}
                    labelMessage={(
                        <FormattedMessage
                            id="mtg-calculators.LandTransferTaxStepInputs.locationLabel"
                            defaultMessage="Location"
                        />
                    )}
                    placeholder={(
                        <FormattedMessage
                            id="mtg-calculators.LandTransferTaxStepInputs.locationPlaceholder"
                            defaultMessage="Enter city"
                        />
                    )}

                    initialProvince={initialProvinceCode}

                    isRequired
                />
            </div>

            <If condition={state.isOneOrMoreCheckboxEnabled}>
                <hr className="rh-horizontal-rule rh-mb-1_5 rh-width-100p" />

                <div className="inlineQuestion">
                    <If condition={state.isFirstTimeBuyerFieldEnabled}>
                        {/* "first time home buyer" checkbox */}
                        <Question
                            field={{
                                name: 'isFirstTimeHomeBuyer',
                                type: 'boolean',
                                value: state.values.isFirstTimeBuyer,
                            }}
                            labelMessage={(
                                <FormattedMessage
                                    id="mtg-calculators.LandTransferTaxStepInputs.firstTimeHomeBuyerLabel"
                                    defaultMessage="I’m a first time home buyer"
                                />
                            )}
                            type="checkbox"
                        />
                    </If>

                    <If condition={state.isNewHomeFieldEnabled}>
                        {/* "newly built home" checkbox */}
                        <Question
                            field={{
                                name: 'isNewlyBuiltHome',
                                type: 'boolean',
                            }}
                            labelMessage={(
                                <FormattedMessage
                                    id="mtg-calculators.LandTransferTaxStepInputs.newlyBuiltHomeLabel"
                                    defaultMessage="I’m buying a newly built home"
                                />
                            )}
                            type="checkbox"
                        />
                    </If>
                </div>
            </If>

            <CalculationPanel
                className="calcPanel"
                title={(
                    <FormattedMessage
                        id="mtg-calculators.LandTransferTaxStepInputs.calculationPanelTitle"
                        defaultMessage="Determining your land transfer tax"
                    />
                )}
            >
                <CalculationLineBlock formulas={FORMULAS} />
            </CalculationPanel>

            <If condition={!isProductReferralDisabled && state.isTransferFeeNoticeEnabled}>
                <MessageBanner
                    className="messsageBanner transferFeeNotice"
                    variant={MessageBannerVariants.SUCCESS}
                    iconKey="light-bulb"
                    message={(
                        <FormattedMessage
                            id="mtg-calculators.LandTransferTaxStepInputs.titleTransferFeeNotice"
                            defaultMessage="This province does not have a Land Transfer Tax. You may be required to pay <titleTransferFeeLink>title transfer fees</titleTransferFeeLink>"
                            values={{
                                // Note: will have to update URL based on intl lang if we start supporting FR
                                // eslint-disable-next-line react/no-multi-comp
                                titleTransferFeeLink: chunks => (
                                    <a href={state.transferFeeNoticeUrl}>
                                        {chunks}
                                    </a>
                                ),
                            }}
                        />
                    )}
                />
            </If>

            <If condition={isMessageBannerCTAEnabled}>
                <MessageBanner
                    className="messsageBanner callToAction"
                    variant={MessageBannerVariants.NOTIFICATION}

                    title={messageBannerTitle}
                    message={messageBannerDescription}
                    messageSize="small"

                    ctaMessage={ctaLabel}
                    ctaDataName="mtg-landTransferTaxCalculator-learnMore"
                    onClick={() => state.handleCTAMessageBannerClick(ctaHref)}
                />
            </If>
        </CalculatorStepPanel>
    );
}

const TOOLTIP_WIDTH = '20rem';

const FORMULAS = [
    {
        id: 'provincialTaxCalculation',
        name: 'provincialTaxCalculation',
        label: (
            <FormattedMessage
                id="mtg-calculators.LandTransferTaxStepInputs.provincialTaxCalculationLabel"
                defaultMessage="Provincial"
            />
        ),
        operandValue: (values) => getLandTransferTaxCalculations({
            homePrice: values.askingPrice,
            homeLocation: values.location,
        }, 'provincialTax'),
        tooltipMessage: (
            <FormattedMessage
                id="mtg-calculators.LandTransferTaxStepInputs.provincialTaxCalculationTooltip"
                defaultMessage="Land transfer tax (LTT), typically calculated as a percentage of the purchase price of a home, is required when purchasing a home in Canada. All provinces have a LTT, and the amount varies in each province."
            />
        ),
        tooltipWidth: TOOLTIP_WIDTH,
    },
    {
        id: 'municipalTaxCalculation',
        name: 'municipalTaxCalculation',
        label: (
            <FormattedMessage
                id="mtg-calculators.LandTransferTaxStepInputs.municipalTaxCalculationLabel"
                defaultMessage="Municipal"
            />
        ),
        operandValue: (values) => getLandTransferTaxCalculations({
            homePrice: values.askingPrice,
            homeLocation: values.location,
        }, 'municipalTax'),
        operator: 'plus',
        tooltipMessage: (
            <FormattedMessage
                id="mtg-calculators.LandTransferTaxStepInputs.municipalTaxCalculationTooltip"
                defaultMessage="Some municipalities, like Toronto, levy an additional LTT, which is similarly calculated as a percentage of the purchase price of a home."
            />
        ),
        tooltipWidth: TOOLTIP_WIDTH,
    },
    {
        id: 'rebateCalculation',
        name: 'rebateCalculation',
        label: (
            <FormattedMessage
                id="mtg-calculators.LandTransferTaxStepInputs.rebateCalculationLabel"
                defaultMessage="Rebate"
            />
        ),
        operandValue: (values) => getLandTransferTaxCalculations({
            homePrice: values.askingPrice,
            homeLocation: values.location,
            isFirstTimeBuyer: values.isFirstTimeHomeBuyer,
            isNewlyBuiltHome: values.isNewlyBuiltHome,
        }, 'rebate'),
        operator: 'minus',
        tooltipMessage: (
            <FormattedMessage
                id="mtg-calculators.LandTransferTaxStepInputs.rebateCalculationTooltip"
                defaultMessage="If you are a first-time home buyer in British Columbia or Ontario, you will be eligible for LTT rebates, equal to the value of the LTT up to a maximum amount set by the province."
            />
        ),
        tooltipWidth: TOOLTIP_WIDTH,

        // Tooltip overflows outside of calculator boundaries and gets cut off when rendered via iframe
        ...(isWidget() ? { 'tooltipLocation': TOOLTIP_TOP_LOCATION } : {}),
    },
    {
        id: 'landTransferTaxCalculation',
        name: 'landTransferTaxCalculation',
        label: (
            <FormattedMessage
                id="mtg-calculators.LandTransferTaxStepInputs.landTransferTaxCalculationLabel"
                defaultMessage="Land transfer tax"
            />
        ),
        operandValue: (values) => getLandTransferTaxCalculations({
            homePrice: values.askingPrice,
            homeLocation: values.location,
            isFirstTimeBuyer: values.isFirstTimeHomeBuyer,
            isNewlyBuiltHome: values.isNewlyBuiltHome,
        }, 'landTransferTax'),
        operator: 'equals',
        result: true,
    },
];

function useLocalState() {
    const form = useForm();
    const calculatorState = useCalculator();

    const FIRST_TIME_BUYER_PROVINCES = [ 'ON', 'PE', 'BC' ];

    const [ state ] = useState(() => observable({
        get values() {
            const document = toJS(calculatorState.document?.journey?.document);
            return getSavedFormValues(document);
        },

        get isFirstTimeBuyerFieldEnabled() {
            // REQUIREMENT: show field when user is in Ontario or PEI or BC
            return FIRST_TIME_BUYER_PROVINCES.includes(state.provinceCode);
        },
        get isNewHomeFieldEnabled() {
            // REQUIREMENT: show field when user is in BC
            return state.provinceCode === 'BC';
        },
        get isTransferFeeNoticeEnabled() {
            // REQUIREMENT: show MessageBanner when user is in AB or SK
            return state.provinceCode === 'AB' || state.provinceCode === 'SK';
        },
        get isOneOrMoreCheckboxEnabled() {
            return state.isFirstTimeBuyerFieldEnabled || state.CalculationLineBlockisNewHomeFieldEnabled;
        },
        get provinceCode() {
            return form.values.location?.province.code;
        },
        get transferFeeNoticeUrl() {
            // URL only gets shown when province is AB or SK
            return state.provinceCode === 'AB'
                ? '/land-transfer-tax-alberta'
                : '/land-transfer-tax-saskatchewan';
        },
        handleCTAMessageBannerClick(href) {
            navigateWindowTo(href);
        },
    }));

    return state;
}

function getSavedFormValues(savedCalculatorData) {
    const property = savedCalculatorData?.property;
    const cityName = property?.address?.city;
    const provinceCode = property?.address?.province;

    return {
        askingPrice: property?.homeValue,
        location: (cityName && provinceCode) && {
            name: cityName,
            province: { code: provinceCode },
        },
        isFirstTimeHomeBuyer: savedCalculatorData?.firstTimeHomeBuyer,
    };
}

LandTransferTaxStepInputs.propTypes = {
    stepNumber: PropTypes.number.isRequired,

    initialProvinceCode: PropTypes.string,
    initialCity: LegacyCityPropType,
    initialAskingPrice: PropTypes.number,

    messageBannerTitle: MessagePropType,
    messageBannerDescription: MessagePropType,
    ctaHref: PropTypes.string,
    ctaLabel: MessagePropType,

    isProductReferralDisabled: PropTypes.bool,
};

LandTransferTaxStepInputs.defaultProps = {
    initialProvinceCode: undefined,
    initialCity: undefined,
    initialAskingPrice: undefined,

    messageBannerTitle: undefined,
    messageBannerDescription: undefined,
    ctaHref: undefined,
    ctaLabel: undefined,

    isProductReferralDisabled: false,
};

const GAP = '.75rem';

const StyledLandTransferTaxStepInputs = styled(observer(LandTransferTaxStepInputs))`
    > .inlineQuestion {
        /* attempts to wrap inputs at just a large enough viewport width to keep
        checkbox labels from wrapping */
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));

        margin-left: -${GAP};
        margin-right: -${GAP};

        > * {
            margin-left: ${GAP};
            margin-right: ${GAP};
        }

        &:nth-of-type(2) > * {
            /* ensures good vertical clearance between checkboxes when stacked
            (important especially on touch devices) */
            margin-bottom: ${Sizes.SPACING.ONE};
        }

        .askingPrice,
        .location {
            /* override default margins on text inputs only */
            margin-top: 0;
            margin-bottom: ${Sizes.SPACING.TWO};
        }
    }

    > .calcPanel {
        margin-top: ${Sizes.SPACING.HALF};
        margin-bottom: ${Sizes.SPACING.TWO};
    }

    > .messsageBanner {
        margin-bottom: ${Sizes.SPACING.TWO};
    }
`;

export default StyledLandTransferTaxStepInputs;
